import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useTranslations } from '~i18n/hooks';
import { useApplyPromoCodeMutation } from '~services/api/credits';
import type { TShopPromoCodeResponse } from '~services/api/credits/types';
import { isErrorWithMessageCap } from '~shared/utils/errors';

import { usePromocodeErrorConfig } from '../../hooks/usePromocodeErrorConfig';
import { checkIfApplicable } from '../../utils/checkIfApplicable';
import {
	SDiscount,
	SForm,
	SInfoText,
	SInput,
	SInputOuter,
} from './PromoCodeForm.styles';

type TPromoCodeForm = {
	PromoCode: string;
	sku: string;
};

type TPromoCodeFormProps = {
	onSuccess: (data: TShopPromoCodeResponse) => void;
	sku: string;
	defaultPromocode?: string;
};

export const PromoCodeForm = ({
	onSuccess,
	sku,
	defaultPromocode,
}: TPromoCodeFormProps) => {
	const [discount, setDiscount] = useState<number>(0);
	const {
		watch,
		register,
		setError,
		formState: { errors, isSubmitting, isValid, isSubmitSuccessful },
		handleSubmit,
		clearErrors,
		setValue,
	} = useForm<TPromoCodeForm>({
		mode: 'onChange',
		reValidateMode: 'onSubmit',
		defaultValues: {
			PromoCode: defaultPromocode,
		},
	});
	const promoCode = watch('PromoCode', '');
	const readyForSubmit = isValid && !isSubmitSuccessful;
	const hasData = promoCode.length > 0;
	const hasServerErrors = errors.root?.serverError;
	const hasError = (hasData && !!errors.PromoCode) || !!hasServerErrors;
	const t = useTranslations('Promocode');

	const customErrorConfig = usePromocodeErrorConfig();

	let infoText = t('applyPromocodeText');

	if (hasError) {
		infoText = errors.root?.serverError?.message ?? t('promocodeInvalidText');
	}

	if (isSubmitSuccessful) {
		infoText = errors.root?.serverError?.message ?? t('promocodeAppliedText');
	} else if (readyForSubmit) {
		infoText = t('applyPromocodeButtonTitle');
	}

	const [send] = useApplyPromoCodeMutation();
	const onSubmit: SubmitHandler<TPromoCodeForm> = async (data) => {
		try {
			const resp = await send(data).unwrap();
			onSuccess(resp);
			setValue('PromoCode', data.PromoCode);
			if (!checkIfApplicable(sku, resp.Prices)) {
				setError('root.serverError', {
					type: 'NotFound',
					message: customErrorConfig.NotApplicable,
				});
				setDiscount(0);
				return;
			}
			clearErrors('PromoCode');
			setDiscount(resp?.DiscountPercent ?? 0);
		} catch (err) {
			setValue('PromoCode', data.PromoCode, { shouldDirty: true });
			if (!isErrorWithMessageCap(err)) {
				setError('root.serverError', {});
				return;
			}

			const customError = customErrorConfig[err.data.Error];
			if (customError) {
				setError('root.serverError', {
					type: 'NotFound',
					message: customError,
				});
				return;
			}

			setError('root.serverError', {});
		}
	};

	return (
		<SForm onSubmit={handleSubmit(onSubmit)}>
			<SInputOuter data-value={promoCode}>
				{/* Оставляем кастомный инпут */}
				<SInput
					autoFocus={!isMobile}
					type="text"
					placeholder="XXXXXXXX"
					maxLength={16}
					disabled={isSubmitting || isSubmitSuccessful}
					{...register('PromoCode', {
						required: true,
						maxLength: 16,
						minLength: 6,
						pattern: /^[A-Za-z0-9]+$/,
						onChange: () => {
							if (hasError) {
								clearErrors('root.serverError');
								clearErrors('PromoCode');
							}
						},
					})}
				/>
				{discount > 0 && !hasError ? (
					<SDiscount size="M">
						{t('promocodeSeparator')}
						<span>{t('promocodeDiscountText', { DISCOUNT: discount })}</span>
					</SDiscount>
				) : null}
			</SInputOuter>

			<SInfoText
				onClick={readyForSubmit ? handleSubmit(onSubmit) : undefined}
				hasError={hasData && hasError && !readyForSubmit}
				isActive={readyForSubmit}
				size="M"
			>
				{infoText}
			</SInfoText>
		</SForm>
	);
};
